import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { mfaApi } from '../../lib/api';
import { BRAND } from '../../lib/theme';

interface MfaBackupCodePageProps {
  dark?: boolean;
  onSuccess: () => void;
  onBack: () => void;
  onCancel: () => void;
}

const BACKUP_CODE_REGEX = /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;

/**
 * Recovery path: enter one of the 10 backup codes.
 *
 * - Input format: XXXX-XXXX-XXXX (auto-inserts dashes while typing)
 * - Input is normalised to uppercase before submission
 * - On success: shows remaining backup code count as a warning
 */
export function MfaBackupCodePage({
  dark = false,
  onSuccess,
  onBack,
  onCancel,
}: MfaBackupCodePageProps) {
  const { t } = useTranslation();
  const { mfaState, completeMfa, cancelMfa } = useAuth();

  const [rawCode, setRawCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [remaining, setRemaining] = useState<number | null>(null);

  // Format input as XXXX-XXXX-XXXX while typing (strip dashes, re-insert)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const stripped = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
    const formatted = stripped.match(/.{1,4}/g)?.join('-') ?? stripped;
    setRawCode(formatted);
    setError('');
  };

  const isValid = BACKUP_CODE_REGEX.test(rawCode);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || !mfaState?.userIdHint) return;
    setLoading(true);
    setError('');

    try {
      const result = await mfaApi.verifyBackupCode(mfaState.userIdHint, rawCode);
      setRemaining(result.backup_codes_remaining);

      // Brief delay to show the remaining-codes banner before navigating
      setTimeout(() => {
        completeMfa(result);
        onSuccess();
      }, result.backup_codes_remaining > 0 ? 2000 : 500);
    } catch {
      setError(t('mfa.setup.step_confirm.invalid_code'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    cancelMfa();
    onCancel();
  };

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      alignItems: 'center',
      justifyContent: 'center',
      background: dark ? '#0b101a' : '#f9fafb',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 400,
        background: dark ? '#141b27' : '#fff',
        border: `1px solid ${dark ? '#1e2735' : '#e4e7ec'}`,
        borderRadius: 16,
        padding: 32,
        boxShadow: dark ? 'none' : '0 4px 24px rgba(16,24,40,0.08)',
      }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#f2f4f7' : '#101828', marginBottom: 8 }}>
          {t('mfa.backup.title')}
        </h2>
        <p style={{ fontSize: 14, color: dark ? '#8392a7' : '#667085', marginBottom: 24 }}>
          {t('mfa.backup.subtitle')}
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <input
              type="text"
              value={rawCode}
              onChange={handleInputChange}
              placeholder="XXXX-XXXX-XXXX"
              autoFocus
              maxLength={14} // 12 chars + 2 dashes
              aria-label={t('mfa.backup.title')}
              aria-invalid={!!error}
              style={{
                width: '100%',
                fontFamily: 'monospace',
                fontSize: 20,
                fontWeight: 600,
                letterSpacing: 4,
                textAlign: 'center',
                padding: '12px 16px',
                border: `2px solid ${error ? '#f04438' : '#e4e7ec'}`,
                borderRadius: 10,
                background: dark ? '#0f1520' : '#fff',
                color: dark ? '#f2f4f7' : '#101828',
                outline: 'none',
                transition: 'border-color 0.15s',
                boxSizing: 'border-box',
              }}
            />
            <p style={{ fontSize: 11, color: dark ? '#5a6a80' : '#98a2b3', marginTop: 6 }}>
              {t('mfa.backup.format_hint')}
            </p>
          </div>

          {error && (
            <div style={{
              background: '#fef3f2',
              border: '1px solid #fecdca',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 12,
              color: '#b42318',
            }}>
              {error}
            </div>
          )}

          {/* Remaining codes warning */}
          {remaining !== null && remaining <= 3 && (
            <div style={{
              background: '#fffaeb',
              border: '1px solid #fedf89',
              borderRadius: 8,
              padding: '10px 12px',
              fontSize: 12,
              color: '#b54708',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span>{t('mfa.backup.remaining_warning', { count: remaining })}</span>
              <span style={{ color: BRAND, fontWeight: 600 }}>
                {t('mfa.backup.regenerate_link')}
              </span>
            </div>
          )}

          <button
            type="submit"
            disabled={!isValid || loading}
            style={{
              background: isValid && !loading ? BRAND : '#d0d5dd',
              color: '#fff',
              border: 'none',
              borderRadius: 9,
              padding: '11px',
              fontSize: 14,
              fontWeight: 600,
              cursor: isValid && !loading ? 'pointer' : 'not-allowed',
              fontFamily: 'Outfit, sans-serif',
            }}
          >
            {loading ? '…' : t('common.action.confirm')}
          </button>
        </form>

        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            type="button"
            onClick={onBack}
            style={linkBtnStyle}
          >
            ← {t('mfa.challenge.back_to_code')}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            style={linkBtnStyle}
          >
            {t('common.action.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

const linkBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: 13,
  color: '#667085',
  cursor: 'pointer',
  textAlign: 'center',
  padding: '4px 0',
};
